import React, { useEffect, useRef, useState } from 'react';
import { Link, Modal } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'use-toast-mui';
import { checkAppUsername } from '../../api/temporaryUser';
import { directMemberAppLink, searchMemberAppLinkAccounts, setMemberAppAccount } from '../../api/memberAppLink';
import {
  AppLinkAccountCard,
  AppLinkAccountId,
  AppLinkAccountList,
  AppLinkAccountMark,
  AppLinkAccountSub,
  AppLinkBody,
  AppLinkContainer,
  AppLinkDupButton,
  AppLinkFooter,
  AppLinkFooterActions,
  AppLinkHeader,
  AppLinkHeaderClose,
  AppLinkHeaderTitle,
  AppLinkFieldGroup,
  AppLinkFieldLabelRow,
  AppLinkGrid,
  AppLinkHint,
  AppLinkInput,
  AppLinkInputRow,
  AppLinkPanel,
  AppLinkResultCount,
  AppLinkSearchButton,
  AppLinkTab,
  AppLinkTabs,
  AppLinkTermsRow,
  AppLinkTermsSection,
  AppLinkTermsTitleRow,
  AppLinkTermsAllRow,
  AppLinkTermsBadge,
  AppLinkTermsLabel,
  FieldLabel,
  RequiredBadge,
  SectionSubText,
} from './TemporaryUserStyled';
import CommonModalButton from '../../form-components/CommonModalButton';
import CommonModalLayout from '../../form-components/CommonModalLayout';
import { APP_PRIVACY_POLICY, APP_TERMS_OF_SERVICE } from '../../constants/appTerms';

const STATUS_LINKED = 'LINKED';

// 회원앱 가입과 동일한 필수 약관. 동의 이력은 저장하지 않고 진행 게이트로만 쓴다.
const TERMS = [
  { key: 'privacy', label: '개인정보 수집 및 이용에 동의합니다.', title: '개인정보처리 방침', content: APP_PRIVACY_POLICY },
  { key: 'tos', label: '서비스 이용약관에 동의합니다.', title: '서비스 이용약관', content: APP_TERMS_OF_SERVICE },
];

// 서버 AppPasswordRule 과 같은 규칙
// 회원앱 비밀번호는 4~16자, 조합 제한 없음(2026-10-28, 서버 AppPasswordRule 과 같음)
const PASSWORD_PATTERN = /^.{4,16}$/u;
const PASSWORD_RULE_MESSAGE = '비밀번호는 4~16자로 입력해 주세요.';
// 서버 AppPasswordRule.isValidUsername 과 같은 규칙(영문·숫자 4~20자, 2026-10-27)
const isValidUsername = value => /^[A-Za-z0-9]{4,20}$/.test(value);
const USERNAME_RULE_MESSAGE = '아이디는 영문·숫자 4~20자로 입력해 주세요.';

const maskId = id => {
  if (!id) return '';
  if (id.length <= 4) return `${id.slice(0, 1)}****`;
  return `${id.slice(0, 3)}****${id.slice(-1)}`;
};

/**
 * 앱 연동 팝업. 신규 가입은 아이디·비밀번호를 만들고, 기존 계정은 정확히 일치하는 앱 계정에 초대를 보낸다.
 * target: {type: 'MEMBER' | 'TEMPORARY_USER', id, name, phoneNumber}
 * - 회원 신규 가입은 바로 저장한다.
 * - 잠재회원 신규 가입은 onApply 로 넘겨 부모 폼이 저장할 때 계정을 만든다(2026-10-22, 예전엔 예약).
 * - 기존 계정 초대는 대상이 저장돼 있으면 바로 보내고, 등록 전(target.id 없음)이면 onApply 로 넘겨 저장 후 보낸다.
 */
const AppLinkModal = ({ open, onClose, target, onApply }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);
  const isMember = target?.type === 'MEMBER';

  const [tab, setTab] = useState('new');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [searched, setSearched] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [agreed, setAgreed] = useState({ privacy: false, tos: false });
  const [viewingTerm, setViewingTerm] = useState(null);
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);

  // 이미 앱 계정이 있는 회원(합친 회원·센터가 만든 계정)은 이 지점만 미연동이다 — 자기 계정이나 본인의 다른 계정으로 초대하거나,
  // 새 아이디를 만들어 갈아탄다(2026-11-02, 데이터가 새 아이디로 옮겨지고 기존 아이디는 빈 계정)
  const hasOwnAccount = isMember && !!target?.appLoginEnabled && !!target?.appUsername;

  useEffect(() => {
    if (!open) return;
    setTab(hasOwnAccount ? 'exist' : 'new');
    setUsername('');
    setPassword('');
    setPasswordConfirm('');
    setUsernameChecked(false);
    setKeyword('');
    setAccounts([]);
    setSearched(false);
    setSelectedAccount(null);
    setAgreed({ privacy: false, tos: false });
    setMergeConfirmOpen(false);
    // 모달이 열릴 때만 초기화한다. target 이 갱신될 때마다 입력 중인 값을 지우면 안 된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const invalidateLinkQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['memberAppLinkHistory'] });
    queryClient.invalidateQueries({ queryKey: ['user-detail'] });
    queryClient.invalidateQueries({ queryKey: ['temporaryUser'] });
  };

  const accountMutation = useMutation({
    // 지금 보고 있는 지점도 연동한다(상품이 없어도, 2026-10-22)
    mutationFn: () =>
      setMemberAppAccount(target.id, {
        username,
        password,
        branchId: target.branchId ?? user.branch?.branchId,
        // 이미 앱 계정이 있는 회원은 확인 창을 거쳐 새 아이디로 갈아탄다
        ...(hasOwnAccount ? { replaceExisting: true } : {}),
      }),
    onSuccess: result => {
      toast.success(hasOwnAccount ? '새 아이디로 연동했습니다.' : '앱 계정을 만들었습니다.');
      invalidateLinkQueries();
      onClose();
      // 새 아이디로 갈아타면 데이터가 새 계정의 회원으로 옮겨 가므로 그 회원 화면으로 간다(기존 회원 화면은 비어 있다)
      if (result?.memberId && result.memberId !== target.id) {
        navigate(`/member/${result.memberId}`);
      }
    },
  });

  const navigate = useNavigate();
  const inviteMutation = useMutation({
    mutationFn: () =>
      directMemberAppLink({
        // 칩이 보는 지점과 같은 지점으로 초대한다(회원 상세에서 고른 지점, 없으면 로그인 지점)
        branchId: target.branchId ?? user.branch?.branchId,
        appUserId: selectedAccount.userId,
        targetType: target.type,
        ...(isMember ? { targetMemberId: target.id } : { targetTemporaryUserId: target.id }),
      }),
    onSuccess: result => {
      // 회원이 이미 연동 요청을 보낸 상태였다면 초대가 아니라 바로 연동된다.
      toast.success(
        result?.status === STATUS_LINKED ? '연동되었습니다.' : '초대를 보냈습니다. 회원이 앱에서 동의하면 연동됩니다.',
      );
      if (result?.warning) toast.warning(result.warning);
      invalidateLinkQueries();
      onClose();
      // 바로 합쳐졌으면 남는 회원은 앱 계정 쪽이다. 보던 회원은 탈퇴 처리되므로 남는 회원 화면으로 옮긴다.
      if (result?.status === STATUS_LINKED && isMember && result?.appMemberId && result.appMemberId !== target.id) {
        navigate(`/member/${result.appMemberId}?tab=one`);
      }
    },
  });

  // 더블클릭이 리렌더 전에 두 번 들어오는 것을 막는다(isPending 은 리렌더 후에야 반영된다).
  const isSubmitting = useRef(false);
  const runMutation = mutation => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    mutation.mutate(undefined, { onSettled: () => (isSubmitting.current = false) });
  };

  const allAgreed = TERMS.every(term => agreed[term.key]);

  const handleToggleAll = () => {
    const next = !allAgreed;
    setAgreed({ privacy: next, tos: next });
  };

  const handleCheckUsername = async () => {
    if (!username) {
      toast.error('아이디를 입력해 주세요');
      return;
    }
    if (!isValidUsername(username)) {
      toast.error(USERNAME_RULE_MESSAGE);
      return;
    }
    try {
      const result = await checkAppUsername(username, isMember ? null : target?.id);
      if (!result?.available) {
        toast.error('이미 사용 중인 아이디입니다');
        setUsernameChecked(false);
        return;
      }
      setUsernameChecked(true);
      toast.success('사용할 수 있는 아이디입니다');
    } catch {
      toast.error('아이디 확인에 실패했습니다');
    }
  };

  const handleSearch = async () => {
    const trimmed = keyword.trim();
    if (!trimmed) {
      toast.error('검색어를 입력해 주세요');
      return;
    }
    try {
      // 아이디 전부 일치만 조회된다(전화번호 검색은 다른 회사 회원까지 찾을 수 있어 없앴다)
      const results = await searchMemberAppLinkAccounts({ username: trimmed });
      setAccounts(results || []);
      setSearched(true);
      setSelectedAccount(null);
    } catch {
      toast.error('앱 계정 검색에 실패했습니다');
    }
  };

  const handleSubmit = () => {
    if (tab === 'new') {
      if (!allAgreed) return;
      if (!username || !password) {
        toast.error('아이디와 비밀번호를 입력해 주세요');
        return;
      }
      if (!usernameChecked) {
        toast.error('아이디 중복확인을 해주세요');
        return;
      }
      if (!PASSWORD_PATTERN.test(password)) {
        toast.error(PASSWORD_RULE_MESSAGE);
        return;
      }
      if (password !== passwordConfirm) {
        toast.error('비밀번호가 일치하지 않습니다');
        return;
      }
      if (isMember && hasOwnAccount) {
        // 새 아이디로 데이터가 모두 옮겨지므로 한 번 더 확인한다
        setReplaceConfirmOpen(true);
      } else if (isMember) {
        runMutation(accountMutation);
      } else {
        onApply({ appLinkType: 'NEW', username, password });
      }
      return;
    }

    if (!selectedAccount) {
      toast.error('초대할 계정을 선택해 주세요');
      return;
    }
    if (!target?.id) {
      onApply({ appLinkType: 'EXISTING', appUserId: selectedAccount.userId, username: maskId(selectedAccount.username) });
      return;
    }
    // 이 회원의 기존 아이디와 다른 계정을 고르면 두 계정이 합쳐지므로 한 번 더 확인한다
    if (hasOwnAccount && selectedAccount.username !== target.appUsername) {
      setMergeConfirmOpen(true);
      return;
    }
    runMutation(inviteMutation);
  };

  const handleConfirmReplace = () => {
    setReplaceConfirmOpen(false);
    runMutation(accountMutation);
  };

  const handleConfirmMerge = () => {
    setMergeConfirmOpen(false);
    runMutation(inviteMutation);
  };

  return (
    <>
      <Modal open={open} onClose={onClose} aria-labelledby="app-link-modal">
        <AppLinkContainer>
          <AppLinkHeader>
            <AppLinkHeaderTitle>APP 연동</AppLinkHeaderTitle>
            <AppLinkHeaderClose onClick={onClose}>✕</AppLinkHeaderClose>
          </AppLinkHeader>

          <AppLinkBody>
            <AppLinkPanel>
              <AppLinkTabs>
                {/* 이미 앱 계정이 있는 회원도 새 아이디를 만들 수 있다(2026-11-02) — 저장 전 확인 창 */}
                <AppLinkTab isActive={tab === 'new'} onClick={() => setTab('new')}>
                  신규 가입
                </AppLinkTab>
                <AppLinkTab isActive={tab === 'exist'} onClick={() => setTab('exist')}>
                  기존 계정
                </AppLinkTab>
              </AppLinkTabs>

              {tab === 'new' ? (
                <AppLinkGrid>
                  <AppLinkFieldGroup>
                    <AppLinkFieldLabelRow>
                      <FieldLabel>아이디</FieldLabel>
                      <RequiredBadge>필수</RequiredBadge>
                    </AppLinkFieldLabelRow>
                    <AppLinkInputRow>
                      <AppLinkInput
                        name="applink-new-username"
                        autoComplete="off"
                        placeholder="아이디를 입력해 주세요"
                        value={username}
                        onChange={e => {
                          setUsername(e.target.value);
                          setUsernameChecked(false);
                        }}
                      />
                      <AppLinkDupButton type="button" onClick={handleCheckUsername}>
                        중복확인
                      </AppLinkDupButton>
                    </AppLinkInputRow>
                  </AppLinkFieldGroup>

                  <AppLinkFieldGroup>
                    <AppLinkFieldLabelRow>
                      <FieldLabel>비밀번호</FieldLabel>
                      <RequiredBadge>필수</RequiredBadge>
                    </AppLinkFieldLabelRow>
                    <AppLinkInput
                      type="password"
                      name="applink-new-password"
                      autoComplete="new-password"
                      placeholder="비밀번호 입력"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                    />
                  </AppLinkFieldGroup>

                  <AppLinkFieldGroup>
                    <AppLinkFieldLabelRow>
                      <FieldLabel>비밀번호 확인</FieldLabel>
                    </AppLinkFieldLabelRow>
                    <AppLinkInput
                      type="password"
                      name="applink-new-password-confirm"
                      autoComplete="new-password"
                      placeholder="비밀번호 재입력"
                      value={passwordConfirm}
                      onChange={e => setPasswordConfirm(e.target.value)}
                    />
                    {/* 입력하는 동안 바로 알린다 — [적용]을 눌러야 토스트로만 알던 것(2026-10-23) */}
                    {passwordConfirm && password !== passwordConfirm && (
                      <AppLinkHint style={{ color: '#ef0c23' }}>비밀번호가 일치하지 않습니다.</AppLinkHint>
                    )}
                  </AppLinkFieldGroup>

                  <AppLinkHint>· 4~16자(숫자만 써도 됩니다)</AppLinkHint>
                  {hasOwnAccount && (
                    <AppLinkHint>
                      · 이미 앱 아이디({target.appUsername})가 있는 회원입니다. 새 아이디를 만들면 이 회원의 데이터가 새 아이디로
                      옮겨지고, 쓰던 아이디는 로그인만 되는 빈 계정으로 남습니다.
                    </AppLinkHint>
                  )}
                </AppLinkGrid>
              ) : (
                <AppLinkFieldGroup>
                  <FieldLabel>앱 아이디 검색</FieldLabel>
                  <AppLinkInputRow>
                    <AppLinkInput
                      name="applink-search-keyword"
                      autoComplete="off"
                      placeholder="회원 아이디"
                      value={keyword}
                      onChange={e => setKeyword(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleSearch();
                      }}
                    />
                    <AppLinkSearchButton type="button" onClick={handleSearch}>
                      검색
                    </AppLinkSearchButton>
                  </AppLinkInputRow>

                  {/* 이 회원 기록에 이미 앱 아이디가 달려 있다(앱 가입 회원, 이미 [신규 가입]으로 아이디를 받은 회원, 다지점 이용 회원 등)
                      — 새 아이디는 못 만들고, 이 아이디나 본인이 쓰는 다른 아이디(합치기)로 연동한다(2026-10-28) */}
                  {hasOwnAccount && (
                    <AppLinkHint>
                      · 이미 앱 아이디({target.appUsername})가 있는 회원입니다. 이 아이디로 연동하거나, 본인이 쓰는 다른 아이디를 검색해 연결할 수 있습니다.
                    </AppLinkHint>
                  )}

                  {searched && (
                    <AppLinkResultCount>
                      검색 결과 {accounts.length}건{accounts.length > 0 && ' · 초대할 계정을 선택하세요'}
                    </AppLinkResultCount>
                  )}

                  <AppLinkAccountList>
                    {accounts.map(account => (
                      <AppLinkAccountCard
                        key={account.userId}
                        isActive={selectedAccount?.userId === account.userId}
                        onClick={() => setSelectedAccount(account)}>
                        <div>
                          <AppLinkAccountId>{maskId(account.username)}</AppLinkAccountId>
                          <AppLinkAccountSub>
                            {account.name} · {account.phoneNumber || '-'}
                          </AppLinkAccountSub>
                        </div>
                        <AppLinkAccountMark>{selectedAccount?.userId === account.userId ? '✓' : ''}</AppLinkAccountMark>
                      </AppLinkAccountCard>
                    ))}
                  </AppLinkAccountList>
                </AppLinkFieldGroup>
              )}
            </AppLinkPanel>

            {tab === 'new' && (
              <AppLinkTermsSection>
                <AppLinkTermsTitleRow>
                  <FieldLabel>가입약관 동의</FieldLabel>
                  <SectionSubText>회원앱 가입과 동일한 필수 약관입니다.</SectionSubText>
                </AppLinkTermsTitleRow>

                {/* label 이라 글자를 눌러도 체크박스 onChange 한 번만 돈다(행 onClick 을 같이 두면 두 번 바뀌어 원래대로) */}
                <AppLinkTermsAllRow isActive={allAgreed}>
                  <input type="checkbox" checked={allAgreed} onChange={handleToggleAll} />
                  <span>전체동의</span>
                </AppLinkTermsAllRow>

                <div>
                  {TERMS.map(term => (
                    <AppLinkTermsRow key={term.key}>
                      <input
                        type="checkbox"
                        checked={!!agreed[term.key]}
                        onChange={() => setAgreed(prev => ({ ...prev, [term.key]: !prev[term.key] }))}
                      />
                      <AppLinkTermsLabel>
                        <AppLinkTermsBadge>[필수]</AppLinkTermsBadge> {term.label}
                      </AppLinkTermsLabel>
                      <Link
                        component="button"
                        type="button"
                        underline="always"
                        onClick={() => setViewingTerm(term)}
                        sx={{ ml: 'auto', flexShrink: 0, fontSize: '12px', color: '#737373' }}>
                        보기
                      </Link>
                    </AppLinkTermsRow>
                  ))}
                </div>
              </AppLinkTermsSection>
            )}
          </AppLinkBody>

          <AppLinkFooter>
            <AppLinkFooterActions>
              <CommonModalButton variant="cancel" onClick={onClose}>
                취소
              </CommonModalButton>
              <CommonModalButton
                variant="submit"
                onClick={handleSubmit}
                disabled={(tab === 'new' && !allAgreed) || accountMutation.isPending || inviteMutation.isPending}>
                {tab === 'exist' ? (target?.id ? '초대 보내기' : '적용') : isMember ? '저장' : '적용'}
              </CommonModalButton>
            </AppLinkFooterActions>
          </AppLinkFooter>
        </AppLinkContainer>
      </Modal>
      <CommonModalLayout
        open={mergeConfirmOpen}
        onClose={() => setMergeConfirmOpen(false)}
        title="다른 아이디와 합칩니다"
        width={480}
        footer={
          <>
            <CommonModalButton variant="cancel" onClick={() => setMergeConfirmOpen(false)}>
              취소
            </CommonModalButton>
            <CommonModalButton variant="delete" onClick={handleConfirmMerge}>
              합치기 초대 보내기
            </CommonModalButton>
          </>
        }>
        <div style={{ fontSize: '14px', lineHeight: 1.7, color: '#262626' }}>
          이 회원의 상품·결제·포인트(다른 지점 것 포함)가 모두 {selectedAccount?.username} 로 옮겨집니다. 이 회원이 쓰던 아이디{' '}
          {target?.appUsername} 로도 앱 로그인은 계속 되지만, 그 아이디에서는 아무 데이터도 보이지 않습니다. 연동 해제로 되돌릴 수
          없으니 같은 사람이 맞는지 확인해 주세요.
        </div>
      </CommonModalLayout>
      <CommonModalLayout
        open={replaceConfirmOpen}
        onClose={() => setReplaceConfirmOpen(false)}
        title="새 아이디로 갈아탑니다"
        width={480}
        footer={
          <>
            <CommonModalButton variant="cancel" onClick={() => setReplaceConfirmOpen(false)}>
              취소
            </CommonModalButton>
            <CommonModalButton variant="delete" onClick={handleConfirmReplace}>
              새 아이디 만들기
            </CommonModalButton>
          </>
        }>
        <div style={{ fontSize: '14px', lineHeight: 1.7, color: '#262626' }}>
          새 아이디 {username} 를 만들고, 이 회원의 상품·결제·포인트(다른 지점 것 포함)와 지점 연동을 모두 새 아이디로 옮깁니다. 이
          회원이 쓰던 아이디 {target?.appUsername} 로도 앱 로그인은 계속 되지만, 그 아이디에서는 아무 데이터도 보이지 않습니다.
          회원에게 새 아이디와 비밀번호를 꼭 알려 주세요.
        </div>
      </CommonModalLayout>
      <CommonModalLayout
        open={!!viewingTerm}
        onClose={() => setViewingTerm(null)}
        title={viewingTerm?.title}
        width={640}
        footer={
          <CommonModalButton variant="submit" onClick={() => setViewingTerm(null)}>
            확인
          </CommonModalButton>
        }>
        <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: 1.7, color: '#374151' }}>
          {viewingTerm?.content}
        </div>
      </CommonModalLayout>
    </>
  );
};

export default AppLinkModal;
